import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

let appInsights;
let reactPlugin;

export const initializeAppInsights = () => {
  let connectionString = process.env.NEXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING;

  // Only initialize in browser environment with connection string
  if (typeof window === 'undefined' || !connectionString) {
    return null;
  }

  // Sanitize connection string (remove potential quotes and whitespace from env vars)
  connectionString = connectionString.trim().replace(/^["']|["']$/g, '');

  // Prevent duplicate initialization (e.g., due to SPA navigations / repeated effects)
  if (appInsights) {
    return appInsights;
  }

  try {
    reactPlugin = new ReactPlugin();

    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            history: null // Will be set later if needed
          }
        },
        // Safe configuration - client-side only
        enableAutoRouteTracking: false,
        enablePageLoadTiming: true, // Track page load performance
        enableCorsCorrelation: false, // Avoid CORS issues
        enableRequestHeaderTracking: false, // Client-side only
        enableResponseHeaderTracking: false, // Client-side only
        enableAjaxErrorStatusText: true,
        enableAjaxPerfTracking: true,
        maxAjaxCallsPerView: 10, // Conservative limit
        disableExceptionTracking: false,
        disableTelemetry: false,
        verboseLogging: false, // Avoid console spam
        // Don't interfere with existing error handling
        enableUnhandledPromiseRejectionTracking: false
      }
    });

    appInsights.loadAppInsights();

    // Set up global API tracker for route timing coordination
    window.appInsightsApiTracker = {
      onApiStart: null,
      onApiEnd: null
    };

    console.log('Application Insights initialized successfully');
    return appInsights;
  } catch (error) {
    console.warn('Application Insights initialization failed:', error);
    return null;
  }
};

export const getAppInsights = () => appInsights;
export const getReactPlugin = () => reactPlugin;

// Safe logging utilities - fallback to console if AI not available
export const logInfo = (message, properties = {}) => {
  try {
    if (appInsights) {
      appInsights.trackTrace({
        message,
        severityLevel: 1, // Information
        properties
      });
    }
  } catch (error) {
    console.warn('Application Insights logging failed:', error);
  }
  // Always log to console as backup
  console.log(message, properties);
};

export const logError = (error, properties = {}) => {
  try {
    if (appInsights) {
      appInsights.trackException({
        exception: error,
        properties
      });
    }
  } catch (aiError) {
    console.warn('Application Insights error tracking failed:', aiError);
  }
  // Always log to console as backup
  console.error(error, properties);
};

export const trackEvent = (name, properties = {}) => {
  // Push to GTM dataLayer for GA4 tracking
  try {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: name,
        ...properties
      });

      // Log in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log(`📊 Track Event: ${name}`, properties);
      }
    }
  } catch (error) {
    console.warn('GTM dataLayer push failed:', error);
  }

  // Also send to Application Insights
  try {
    if (appInsights) {
      appInsights.trackEvent({
        name,
        properties
      });
    }
  } catch (error) {
    console.warn('Application Insights event tracking failed:', error);
  }
};
